import type { PipLayout } from "../core/types";
import { fitInside, gridRects, pipAspect, pipRect, type CropRect, type Rect } from "./layout";

/**
 * Pumps the newest frame from one track. Frames are VideoFrames and hold GPU
 * memory until closed, so exactly one is kept and every replaced frame is
 * released here.
 */
class FramePump {
  private reader: ReadableStreamDefaultReader<VideoFrame> | null = null;
  private latest: VideoFrame | null = null;
  private stopped = false;
  private pump: Promise<void> | null = null;
  received = 0;
  readonly track: MediaStreamTrack;

  private onFrame: (() => void) | null;

  constructor(track: MediaStreamTrack, onFrame?: () => void) {
    this.track = track;
    this.onFrame = onFrame ?? null;
  }

  start(): void {
    if (this.pump) return;
    const processor = new MediaStreamTrackProcessor({ track: this.track });
    this.reader = processor.readable.getReader();
    this.pump = (async () => {
      while (!this.stopped) {
        let result: ReadableStreamReadResult<VideoFrame>;
        try {
          result = await this.reader!.read();
        } catch {
          break;
        }
        if (result.done) break;
        this.received += 1;
        const frame = result.value;
        if (this.latest) this.latest.close();
        this.latest = frame;
        this.onFrame?.();
      }
      if (this.latest) {
        this.latest.close();
        this.latest = null;
      }
    })();
  }

  /** Hands over the newest frame; the caller owns it and must close it. */
  take(): VideoFrame | null {
    const frame = this.latest;
    this.latest = null;
    return frame;
  }

  get hasFrame(): boolean {
    return this.latest !== null;
  }

  async stop(): Promise<void> {
    this.stopped = true;
    try {
      await this.reader?.cancel();
    } catch {
      // Already cancelled.
    }
    this.reader = null;
    if (this.latest) {
      this.latest.close();
      this.latest = null;
    }
    await this.pump;
    this.pump = null;
  }
}

export interface StageScreen {
  id: string;
  label: string;
  hasSystemAudio: boolean;
  pump: FramePump;
  thumb: HTMLCanvasElement;
  thumbDrawn: boolean;
  lastThumbAt: number;
  /** Last frame at its fitted size: a static screen sends no new frames, and
   *  the canvas must never flash to black between them. */
  buffer: HTMLCanvasElement | null;
  /** Region of the source that gets recorded; null means the whole screen. */
  crop: CropRect | null;
}

/**
 * The compositor: what the preview shows is exactly what gets recorded,
 * because the recorder captures this canvas. Source frames come straight off
 * the tracks, never through <video> elements — that path depends on Chrome's
 * media element pipeline and silently yields nothing when it stalls.
 */
export class Stage {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private screens: StageScreen[] = [];
  private camera: FramePump | null = null;
  private cameraBuffer: HTMLCanvasElement | null = null;
  private pip: PipLayout;
  private raf = 0;
  private timer = 0;
  private fps = 30;
  private lastDraw = 0;
  private running = false;
  private dirty = true;
  lastDrawError: string | null = null;

  constructor(canvas: HTMLCanvasElement, pip: PipLayout, fps: number) {
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) throw new Error("canvas 2d is unavailable");
    this.canvas = canvas;
    this.ctx = ctx;
    this.pip = pip;
    this.fps = fps;
  }

  setSize(width: number, height: number): void {
    if (this.canvas.width === width && this.canvas.height === height) return;
    this.canvas.width = width;
    this.canvas.height = height;
    this.invalidate();
  }

  setFps(fps: number): void {
    this.fps = fps;
  }

  get screenCount(): number {
    return this.screens.length;
  }

  get cameraOn(): boolean {
    return this.camera !== null;
  }

  listScreens(): StageScreen[] {
    return [...this.screens];
  }

  addScreen(id: string, label: string, stream: MediaStream, hasSystemAudio: boolean): StageScreen {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error("screen stream has no video track");
    const pump = new FramePump(track, this.noteFrame);
    const thumb = document.createElement("canvas");
    thumb.width = 108;
    thumb.height = 61;
    const entry: StageScreen = {
      id,
      label,
      hasSystemAudio,
      pump,
      thumb,
      thumbDrawn: false,
      lastThumbAt: 0,
      buffer: null,
      crop: null,
    };
    pump.start();
    this.screens.push(entry);
    this.invalidate();
    return entry;
  }

  removeScreen(id: string): void {
    const index = this.screens.findIndex((screen) => screen.id === id);
    if (index < 0) return;
    const [removed] = this.screens.splice(index, 1);
    if (removed) void removed.pump.stop();
    this.invalidate();
  }

  setCamera(stream: MediaStream | null): void {
    const previous = this.camera;
    this.camera = null;
    this.cameraBuffer = null;
    void previous?.stop();
    if (stream) {
      const track = stream.getVideoTracks()[0];
      if (track) {
        const pump = new FramePump(track, this.noteFrame);
        pump.start();
        this.camera = pump;
      }
    }
    this.invalidate();
  }

  /** Records only this region of a screen (fractions of the source frame). */
  setCrop(id: string, crop: CropRect | null): void {
    const screen = this.screens.find((entry) => entry.id === id);
    if (!screen) return;
    screen.crop = crop;
    screen.buffer = null;
    screen.thumbDrawn = false;
    screen.lastThumbAt = 0;
    this.invalidate();
  }

  getCrop(id: string): CropRect | null {
    return this.screens.find((entry) => entry.id === id)?.crop ?? null;
  }

  /** The last frame of one screen, for the area picker. */
  bufferFor(id: string): HTMLCanvasElement | null {
    return this.screens.find((entry) => entry.id === id)?.buffer ?? null;
  }

  setPip(pip: PipLayout): void {
    this.pip = pip;
    this.invalidate();
  }

  getPip(): PipLayout {
    return this.pip;
  }

  /**
   * Camera with no screen behind it: the camera is the picture, edge to edge.
   * A corner PiP over an empty frame is not a take anyone wants.
   */
  get cameraOnly(): boolean {
    return this.camera !== null && this.screens.length === 0;
  }

  /** PiP box in canvas pixels — used for hit-testing drags. */
  pipBox(): Rect | null {
    if (!this.camera || this.cameraOnly) return null;
    return pipRect(this.pip, this.canvas.width, this.canvas.height);
  }

  hitPip(canvasX: number, canvasY: number): boolean {
    const box = this.pipBox();
    if (!box) return false;
    return (
      canvasX >= box.x &&
      canvasX <= box.x + box.w &&
      canvasY >= box.y &&
      canvasY <= box.y + box.h
    );
  }

  invalidate(): void {
    this.dirty = true;
  }

  /** True when any source has delivered at least one frame. */
  get hasLiveFrames(): boolean {
    return this.screens.some((screen) => screen.buffer !== null) || this.cameraBuffer !== null;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.dirty = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    this.restartDriver();
  }

  stop(): void {
    this.running = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.timer) window.clearInterval(this.timer);
    this.timer = 0;
  }

  private onVisibility = (): void => {
    if (this.running) this.restartDriver();
  };

  /**
   * A background tab has its animation frames throttled to a crawl, which
   * would freeze the recorded picture the moment the user switches away. When
   * hidden, draw from a timer instead — and from arriving frames, since a
   * hidden timer is throttled too.
   */
  private restartDriver(): void {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    if (this.timer) window.clearInterval(this.timer);
    this.timer = 0;
    if (!this.running) return;
    if (document.hidden) {
      this.timer = window.setInterval(() => this.drawIfDue(performance.now()), 1000 / this.fps);
      return;
    }
    const loop = (time: number) => {
      if (!this.running) return;
      this.drawIfDue(time);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  private drawIfDue(time: number): void {
    const interval = 1000 / this.fps;
    if (!this.dirty && time - this.lastDraw < interval) return;
    try {
      this.draw();
    } catch (error) {
      this.lastDrawError = error instanceof Error ? `${error.message} ${error.stack ?? ""}` : String(error);
      console.error("stage draw failed", error);
    }
    this.lastDraw = time;
    this.dirty = false;
  }

  /** Called whenever a source hands over a new frame. */
  private noteFrame = (): void => {
    this.dirty = true;
    if (document.hidden) this.drawIfDue(performance.now());
  };

  /** Draw one frame. Public so the recorder can prime the first frame. */
  draw(): void {
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    ctx.fillStyle = "#0d0b09";
    ctx.fillRect(0, 0, width, height);

    const tiles = gridRects(this.screens.length, width, height);
    const now = Date.now();
    for (let i = 0; i < this.screens.length; i++) {
      const screen = this.screens[i];
      const tile = tiles[i];
      if (!screen || !tile) continue;

      const frame = screen.pump.take();
      if (frame) {
        try {
          const source = screen.crop
            ? {
                x: Math.round(screen.crop.x * frame.displayWidth),
                y: Math.round(screen.crop.y * frame.displayHeight),
                w: Math.max(2, Math.round(screen.crop.w * frame.displayWidth)),
                h: Math.max(2, Math.round(screen.crop.h * frame.displayHeight)),
              }
            : { x: 0, y: 0, w: frame.displayWidth, h: frame.displayHeight };
          const dest = fitInside({ width: source.w, height: source.h }, tile);
          const buffer = this.ensureBuffer(screen.buffer, dest.w, dest.h);
          screen.buffer = buffer;
          const bufferCtx = buffer.getContext("2d");
          if (bufferCtx) {
            bufferCtx.drawImage(frame, source.x, source.y, source.w, source.h, 0, 0, dest.w, dest.h);
            if (now - screen.lastThumbAt > 500) {
              screen.lastThumbAt = now;
              const thumbCtx = screen.thumb.getContext("2d");
              if (thumbCtx) {
                thumbCtx.clearRect(0, 0, 108, 61);
                thumbCtx.drawImage(buffer, 0, 0, 108, 61);
                screen.thumbDrawn = true;
              }
            }
          }
        } catch (error) {
          // A frame can be invalidated by a track change; the next one recovers.
          this.lastDrawError = error instanceof Error ? error.message : String(error);
        } finally {
          frame.close();
        }
      }

      if (screen.buffer) {
        const dest = fitInside(
          { width: screen.buffer.width, height: screen.buffer.height },
          tile,
        );
        try {
          ctx.drawImage(screen.buffer, dest.x, dest.y, dest.w, dest.h);
        } catch (error) {
          this.lastDrawError = error instanceof Error ? error.message : String(error);
        }
      }
    }

    const cameraOnly = this.cameraOnly;
    if (this.camera) {
      const fresh = this.camera.take();
      if (fresh) {
        try {
          const box = cameraOnly
            ? { x: 0, y: 0, w: width, h: height }
            : pipRect(this.pip, width, height);
          const buffer = this.ensureBuffer(this.cameraBuffer, box.w, box.h);
          this.cameraBuffer = buffer;
          const bufferCtx = buffer.getContext("2d");
          if (bufferCtx) this.drawCoverInto(bufferCtx, fresh, box.w, box.h);
        } catch {
          // Frame invalidated mid-change.
        } finally {
          fresh.close();
        }
      }
    }

    if (this.cameraBuffer && cameraOnly) {
      ctx.save();
      if (this.pip.mirrored) {
        ctx.translate(width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(this.cameraBuffer, 0, 0, width, height);
      ctx.restore();
    } else if (this.cameraBuffer) {
      const box = pipRect(this.pip, width, height);
      ctx.save();
      this.pathShape(ctx, box, this.pip.shape);
      ctx.clip();
      if (this.pip.mirrored) {
        ctx.translate(box.x + box.w, box.y);
        ctx.scale(-1, 1);
        ctx.drawImage(this.cameraBuffer, 0, 0, box.w, box.h);
      } else {
        ctx.drawImage(this.cameraBuffer, box.x, box.y, box.w, box.h);
      }
      ctx.restore();

      ctx.save();
      this.pathShape(ctx, box, this.pip.shape);
      ctx.strokeStyle = "rgba(250,247,241,0.92)";
      ctx.lineWidth = Math.max(2, Math.round(height * 0.0035));
      ctx.stroke();
      ctx.restore();
    }
  }

  private ensureBuffer(
    existing: HTMLCanvasElement | null,
    width: number,
    height: number,
  ): HTMLCanvasElement {
    // A window being resized changes the fitted size every frame; reallocating
    // each time churns the GC. Within 2px the stretch is invisible.
    if (
      existing &&
      Math.abs(existing.width - width) <= 2 &&
      Math.abs(existing.height - height) <= 2
    ) {
      return existing;
    }
    const buffer = document.createElement("canvas");
    buffer.width = Math.max(1, width);
    buffer.height = Math.max(1, height);
    return buffer;
  }

  /** Aspect-fill: the camera fills its box, cropped, never stretched. */
  private drawCoverInto(
    ctx: CanvasRenderingContext2D,
    frame: VideoFrame,
    w: number,
    h: number,
  ): void {
    const sourceAspect = frame.displayWidth / frame.displayHeight;
    const targetAspect = w / h;
    let sx = 0;
    let sy = 0;
    let sw = frame.displayWidth;
    let sh = frame.displayHeight;
    if (sourceAspect > targetAspect) {
      sw = Math.round(frame.displayHeight * targetAspect);
      sx = Math.round((frame.displayWidth - sw) / 2);
    } else {
      sh = Math.round(frame.displayWidth / targetAspect);
      sy = Math.round((frame.displayHeight - sh) / 2);
    }
    ctx.drawImage(frame, sx, sy, sw, sh, 0, 0, w, h);
  }

  private pathShape(
    ctx: CanvasRenderingContext2D,
    box: Rect,
    shape: PipLayout["shape"],
  ): void {
    ctx.beginPath();
    if (shape === "circle") {
      const r = Math.min(box.w, box.h) / 2;
      ctx.arc(box.x + box.w / 2, box.y + box.h / 2, r, 0, Math.PI * 2);
    } else {
      const radius = shape === "square" ? 0 : Math.round(Math.min(box.w, box.h) * 0.16);
      ctx.roundRect(box.x, box.y, box.w, box.h, radius);
    }
  }

  /** PNG thumbnail of the current frame, bounded on the long edge. */
  snapshot(maxWidth = 640): string | null {
    const scale = Math.min(1, maxWidth / this.canvas.width);
    const w = Math.max(1, Math.round(this.canvas.width * scale));
    const h = Math.max(1, Math.round(this.canvas.height * scale));
    const out = document.createElement("canvas");
    out.width = w;
    out.height = h;
    const ctx = out.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(this.canvas, 0, 0, w, h);
    try {
      return out.toDataURL("image/jpeg", 0.72);
    } catch {
      return null;
    }
  }

  /** Live thumbnail of one attached screen, for the source list. */
  thumbnailFor(id: string): string | null {
    const screen = this.screens.find((entry) => entry.id === id);
    if (!screen || !screen.thumbDrawn) return null;
    try {
      return screen.thumb.toDataURL("image/jpeg", 0.6);
    } catch {
      return null;
    }
  }

  async release(): Promise<void> {
    this.stop();
    for (const screen of this.screens) await screen.pump.stop();
    this.screens = [];
    const camera = this.camera;
    this.camera = null;
    await camera?.stop();
    this.cameraBuffer = null;
  }
}
